import { formatDurationString } from '@gonfalon/datetime/formatDurationString';
import type { Experiment, Iteration, Treatment } from '@gonfalon/experiments/types';
import { getVariationFillColor } from '@gonfalon/flag-variations/getVariationFillColor';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import type { Flag } from '@gonfalon/instructions/instructionTypes';

import type { StopMenuItemData } from './types';

export const getTargetingRule = ({
  flag,
  experiment,
  iteration,
}: {
  flag?: Flag;
  experiment?: Experiment;
  iteration?: Iteration;
}) => {
  if (!iteration || !experiment || !flag?.environments?.[experiment.environmentKey]?.rules) {
    return undefined;
  }
  const flagsEntries = Object.entries(iteration.flags || {});
  if (flagsEntries.length === 0) {
    return undefined;
  }
  const firstFlagConfig = flagsEntries[0][1];
  const environmentRules = flag.environments[experiment.environmentKey]?.rules;
  return environmentRules?.find((rule) => rule._id === firstFlagConfig?.targetingRule);
};

export const createStopItem =
  (flag: Flag, winningTreatmentId?: string) =>
  (treatment: Treatment, index: number): StopMenuItemData => {
    const [{ variationId }] = treatment.parameters ?? [{}];
    const variationIndex = flag.variations.findIndex((v) => v._id === variationId);
    const color = getVariationFillColor(variationIndex === -1 ? 0 : variationIndex);
    const isControl = treatment.baseline ?? false;
    const isWinning = winningTreatmentId === treatment._id;

    return {
      id: `treatment-${treatment._id ?? index}`,
      name: treatment.name ?? (isControl ? 'Control' : `Treatment ${index + 1}`),
      color,
      isControl,
      isWinning,
      treatmentId: treatment._id ?? '',
    };
  };

export const getDisabledReason = (flag?: FeatureFlag, iteration?: Iteration, experiment?: Experiment) => {
  if (flag?.archived) {
    return `You cannot stop an experiment if its flag is archived`;
  }
  if (!flag) {
    return `You cannot stop an experiment without flag`;
  }
  if (!iteration) {
    return `You cannot stop an experiment without iteration`;
  }
  if (!experiment) {
    return `You cannot stop an experiment without experiment`;
  }
};

export function calculateDuration(startedAt?: number, endedAt?: number, short?: boolean): string {
  if (!startedAt) {
    return '';
  }

  if (short && endedAt) {
    return formatDurationString(endedAt, startedAt);
  }

  const start = new Date(startedAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const startFormatted = format(start, DateFormat.MMM_D_YYYY);

  if (endedAt) {
    const endFormatted = format(end, DateFormat.MMM_D_YYYY);
    return `${diffDays} days, ${startFormatted} to ${endFormatted}`;
  }

  return `${diffDays} days since ${startFormatted}`;
}
